import {
  MobileTable,
  Page,
  PageTitleWithButton,
  Pagination,
  Table,
  Loader
} from '../../../components';
import { Theme, Box, Button } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import makeStyles from '@mui/styles/makeStyles';
import { Add, Delete, Edit } from '@mui/icons-material';
import { ManageMaterialsFilters } from '../manage-materials/manage-materials-filters';
import { isMobile } from 'react-device-detect';
import { getMaterialList, deleteMaterial } from '../../../fetch';
import { useQuery } from 'react-query';
import { IResponse, IMaterialListItem } from '../../../models';
import { useState, useMemo } from 'react';
import { CreateMaterialModal } from './CreateMaterialModal';

export const ManageMaterials = () => {
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [sortDirection, setSortDirection] = useState<{
    MaterialNumber?: 'Asc' | 'Desc';
    Class?: 'Asc' | 'Desc';
    Name?: 'Asc' | 'Desc';
    Active?: 'Asc' | 'Desc';
  }>({
    Name: 'Asc'
  });
  const [selectedSort, setSelectedSort] = useState<string>('name');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [selectedMaterialClassIdFilter, setSelectedMaterialClassIdFilter] = useState(null);
  const [isModalOpen, showModal] = useState<boolean>(false);
  const [isDeleting, setDeleting] = useState<boolean>(false);

  const [filters, setFilters] = useState<{
    materialClassId?: string;
    name?: string;
  }>({});

  const handleEditMaterial = materialId => {
    history.push(`/development/parts/manage-materials/${materialId}/general-information`);
  };

  const handleDeleteMaterial = async materialId => {
    await deleteMaterial(materialId);
    refetchMaterialsList();
  };

  const {
    isLoading: isLoadingMaterialsList,
    data: materials,
    isRefetching: refetchingMaterialsList,
    refetch: refetchMaterialsList
  } = useQuery<IResponse<IMaterialListItem[]>, Error>(
    ['materials-list', page, perPage, filters, selectedSort, sortDirection],
    () =>
      getMaterialList({
        page: page + 1,
        perPage,
        name: searchValue,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort],
        materialClassId: selectedMaterialClassIdFilter
      }),
    {
      onSuccess: optionSets => {
        setRecordCount(optionSets.totalRecordCount);
      }
    }
  );
  const materialsData = useMemo(() => materials?.records ?? [], [materials]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Material Number',
        accessor: 'materialId',
        isServerSorted: selectedSort === 'MaterialNumber',
        isServerSortedDesc: sortDirection.MaterialNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('MaterialNumber')
      },
      {
        Header: 'Class',
        accessor: 'class',
        isServerSorted: selectedSort === 'Class',
        isServerSortedDesc: sortDirection.Class === 'Desc',
        handleClickColumn: () => handleClickColumn('Class')
      },
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Status',
        accessor: 'isActive',
        isServerSorted: selectedSort === 'Active',
        isServerSortedDesc: sortDirection.Active === 'Desc',
        handleClickColumn: () => handleClickColumn('Active'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IMaterialListItem } };
        }) => {
          return <span>{original.isActive ? 'Active' : 'Inactive'}</span>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: ' ',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IMaterialListItem } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                id="edit-material"
                type="button"
                color="primary"
                startIcon={<Edit />}
                onClick={() => handleEditMaterial(original.materialId)}
              >
                Edit
              </Button>
              <Button
                id="delete-material"
                type="button"
                className={classes.deleteButton}
                startIcon={<Delete />}
                onClick={async () => {
                  const result = window.confirm('Are you sure you want to delete this Material?');
                  if (result) {
                    setDeleting(true);
                    try {
                      await handleDeleteMaterial(original.materialId);
                      refetchMaterialsList();
                      enqueueSnackbar('Material Deleted!', {
                        variant: 'success'
                      });
                    } catch (error) {
                      console.log(error);
                      enqueueSnackbar(
                        error?.Detail ?? 'Error deleting Material, please try again',
                        {
                          variant: 'error'
                        }
                      );
                    } finally {
                      setDeleting(false);
                    }
                  }
                }}
              >
                Delete
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialsData]);

  return (
    <>
      {isDeleting && (
        <Loader position="centered" type="fullscreen">
          Deleting...
        </Loader>
      )}
      <Page title="Manage Materials">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Manage Materials'
          }}
          button={
            <Box display="flex">
              <Button
                id="add-option"
                startIcon={<Add />}
                color="primary"
                variant="contained"
                className={classes.addMaterialButton}
                onClick={() => showModal(true)}
              >
                Add Material
              </Button>
            </Box>
          }
        />
        <ManageMaterialsFilters
          isLoading={isLoadingMaterialsList || refetchingMaterialsList}
          setSelectedMaterialClassIdFilter={setSelectedMaterialClassIdFilter}
          handleSearch={(val?: string) => {
            setPage(0);
            setFilters({
              ...filters,
              name: val
            });
          }}
          applyFilters={(clearFilters?: boolean) => {
            if (clearFilters) {
              setFilters({
                name: ''
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                name: searchValue,
                materialClassId: selectedMaterialClassIdFilter
              });
            }
          }}
          setPage={setPage}
          setHasAppliedFilters={setHasAppliedFilters}
          hasAppliedFilters={hasAppliedFilters}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
        <Table
          columns={columns}
          data={materialsData}
          isLoading={isLoadingMaterialsList}
          hidePagination
          containerClasses="materials-table"
          ResponsiveComponent={isMobile ? MobileTable : undefined}
          mobileProps={{
            fields: columns
              .filter(col => col.Header)
              .map(col => ({
                name: col.Header,
                accessor: col.accessor,
                Cell: col?.Cell
              })),
            handleEdit: (selectedMaterial: any) => {
              handleEditMaterial(selectedMaterial);
            },
            handleDelete: (selectedMaterial: any) => {
              handleDeleteMaterial(selectedMaterial.materialId);
            }
          }}
        />
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      </Page>
      <CreateMaterialModal open={isModalOpen} onClose={() => showModal(false)} />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  link: {
    textDecoration: 'none'
  },
  addMaterialButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  actionButtons: {
    textAlign: 'right',
    display: 'flex'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
